import React, { useEffect, useMemo, useState } from "react";
import { Image as PdfImage, StyleSheet, Text, View } from "@react-pdf/renderer";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import usePDFRenderer from "hooks/usePDFRenderer";
import { useAuth } from "context/AuthProvider";
import { RuleRequired } from "utils/MyUtils";
import { MyConst } from "data/MyConst";
import {
  boolText,
  hasPermission,
  showEmpty,
} from "features/kms/system/SystemPageUtils";
import { formatPersonName } from "features/kms/common/nameFormat";
import OrganizationVerificationDialog from "./OrganizationVerificationDialog";

function findOptionByCode(items, code) {
  if (!Array.isArray(items) || !code) return null;
  return (
    items.find(
      (item) =>
        String(item?.kode || "").toUpperCase() === String(code).toUpperCase(),
    ) || null
  );
}

function toDropdownOptions(items, valueKey, labelBuilder) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...item,
    __label: labelBuilder(item),
    __value: item?.[valueKey] ? String(item[valueKey]) : "",
  }));
}

function toAssetUrl(location, filename) {
  if (!filename) return "";
  const base = String(MyConst.URLASET || "").replace(/\/$/, "");
  return `${base}/${location}/${filename}`;
}

function toExternalUrl(value) {
  const url = String(value || "").trim();
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function normalizeUUIDList(value) {
  if (!value) return [];
  const items = Array.isArray(value) ? value : String(value).split(",");
  return items.map((item) => String(item || "").trim()).filter(Boolean);
}

function splitCSVNames(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function orgValue(value) {
  return showEmpty(value);
}

function organizationLogoUrl(filename) {
  return filename ? toAssetUrl("organisasi-logo", filename) : "";
}

function organizationLogoAPIAssetSource(filename) {
  return filename
    ? `${String(MyConst.URLAPI || "").replace(/\/$/, "")}/file/asset/organisasi-logo/${encodeURIComponent(filename)}`
    : "";
}

async function loadOrganizationLogoDataURL(filename, token) {
  const url = organizationLogoAPIAssetSource(filename);
  if (!url) return "";

  try {
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return "";

    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return "";
  }
}

function activePengurusItems(item) {
  return Array.isArray(item?.pengurus_aktif) ? item.pengurus_aktif : [];
}

function formatPengurusName(item) {
  return formatPersonName(item, ["nama_lengkap"]);
}

function OrganizationDetailField({ label, value, size = 4 }) {
  return (
    <div className={`col-md-${size} mb-3`}>
      <div className="text-xs text-uppercase text-muted font-weight-bold">{label}</div>
      <div className="border-bottom pb-2">{orgValue(value)}</div>
    </div>
  );
}

function ActivePengurusTable({ items = [] }) {
  if (items.length < 1) {
    return (
      <div className="text-muted">
        Pengurus aktif belum tersedia.
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-bordered table-sm mb-0">
        <thead>
          <tr>
            <th className="text-center" style={{ width: 48 }}>No</th>
            <th>Nama</th>
            <th>Jabatan</th>
            <th style={{ width: 130 }}>HP</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, index) => (
            <tr key={row?.id_pengurus_organisasi || index}>
              <td className="text-center">{index + 1}</td>
              <td>{orgValue(formatPengurusName(row))}</td>
              <td>{orgValue(row?.jabatan)}</td>
              <td>{orgValue(row?.hp)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OrganizationDetail({ item, onLoaded = () => {}, headerActions = null }) {
  const logoUrl = organizationLogoUrl(item?.logo);
  const isKlub = String(item?.jenis_kode || "").toUpperCase() === "KLUB";
  const pengurusAktif = activePengurusItems(item);

  useEffect(() => {
    if (item?.id_organisasi) {
      onLoaded(item);
    }
  }, [item, onLoaded]);

  return (
    <div className="organization-profile-detail">
      <div className="card bg-light mb-3">
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row align-items-center">
            <div className="text-center">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={item?.nama || "Logo Organisasi"}
                  className="elevation-1 bg-white"
                  style={{
                    width: 112,
                    height: 112,
                    objectFit: "contain",
                    objectPosition: "center",
                    border: "3px solid #fff",
                    borderRadius: 8,
                  }}
                />
              ) : (
                <div
                  className="bg-white border d-flex align-items-center justify-content-center"
                  style={{ width: 112, height: 112, borderRadius: 8 }}
                >
                  <i className="fa fa-building text-muted fa-3x" />
                </div>
              )}
            </div>
            <div className="ml-md-4 mt-3 mt-md-0 text-center text-md-left flex-fill">
              <div className="text-uppercase text-sm font-weight-bold text-muted">
                PROFIL ORGANISASI
              </div>
              <h3 className="mb-1">{orgValue(item?.nama)}</h3>
              <div className="text-muted mb-2">
                <span className="mr-4">
                  <strong>Singkatan</strong>: {orgValue(item?.nama_singkat)}
                </span>
                {item?.cabor_name ? (
                  <span className="mr-4">
                    <strong>Cabor</strong>: {orgValue(item?.cabor_name)}
                  </span>
                ) : null}
              </div>
              {headerActions ? <div className="mt-2">{headerActions}</div> : null}
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body pb-1">
          <div className="row">
            <OrganizationDetailField label="Nama Organisasi" value={item?.nama} size={8} />
            <OrganizationDetailField label="Singkatan" value={item?.nama_singkat} size={4} />
            {isKlub ? (
              <OrganizationDetailField label="Induk Organisasi" value={item?.parent_name} size={12} />
            ) : null}
            <OrganizationDetailField label="Cabor" value={item?.cabor_name} size={12} />
            <OrganizationDetailField label="Alamat" value={item?.alamat} size={12} />
            <OrganizationDetailField label="Telepon" value={item?.telepon} size={4} />
            <OrganizationDetailField label="Email" value={item?.email} size={4} />
            <OrganizationDetailField label="Website" value={item?.website} size={4} />
          </div>
        </div>
      </div>
      <div className="card mt-3">
        <div className="card-body">
          <div className="text-xs text-uppercase text-muted font-weight-bold mb-2">
            Pengurus Aktif
          </div>
          <ActivePengurusTable items={pengurusAktif} />
        </div>
      </div>
    </div>
  );
}

const orgPDFStyles = StyleSheet.create({
  title: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
    marginTop: 2,
  },
  section: {
    marginBottom: 6,
    padding: 4,
  },
  lastSection: {
    marginBottom: 0,
  },
  contentRow: {
    flexDirection: "row",
    width: 515,
  },
  rowsWrap: {
    width: 385,
    paddingRight: 10,
  },
  row: {
    flexDirection: "row",
    width: 385,
    marginBottom: 2.5,
  },
  fullRow: {
    flexDirection: "row",
    width: 515,
    marginBottom: 2.5,
  },
  label: {
    width: 138,
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    lineHeight: 1.25,
  },
  colon: {
    width: 8,
    fontSize: 8.5,
    textAlign: "center",
    lineHeight: 1.25,
  },
  value: {
    width: 239,
    fontSize: 8.5,
    lineHeight: 1.25,
  },
  fullValue: {
    width: 349,
    fontSize: 8.5,
    lineHeight: 1.25,
  },
  logoWrap: {
    width: 130,
    alignItems: "flex-end",
    paddingTop: 0,
    padding: 4,
  },
  logo: {
    width: 82,
    height: 104,
    objectFit: "contain",
    borderWidth: 1,
    borderColor: "#333",
  },
  logoPlaceholder: {
    width: 82,
    height: 104,
    borderWidth: 1,
    borderColor: "#333",
    alignItems: "center",
    justifyContent: "center",
  },
  logoCenterWrap: {
    width: 515,
    alignItems: "center",
    marginTop: -4,
    marginBottom: 10,
  },
  logoCentered: {
    width: 86,
    height: 86,
    objectFit: "contain",
    borderWidth: 1,
    borderColor: "#333",
  },
  logoCenterPlaceholder: {
    width: 86,
    height: 86,
    borderWidth: 1,
    borderColor: "#333",
    alignItems: "center",
    justifyContent: "center",
  },
  muted: {
    color: "#666",
    fontSize: 8,
  },
  historyTable: {
    width: 515,
  },
  historyHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f1f3f5",
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: "#999",
  },
  historyRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderColor: "#999",
  },
  historyCell: {
    fontSize: 7,
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#999",
  },
  historyHeaderCell: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#999",
  },
});

function pdfText(value) {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

const orgPengurusColWidth = {
  no: 28,
  nama: 210,
  jabatan: 160,
  hp: 117,
};

function OrgPDFRow({ label, value, full = false }) {
  return (
    <View style={full ? orgPDFStyles.fullRow : orgPDFStyles.row}>
      <Text style={orgPDFStyles.label}>{label}</Text>
      <Text style={orgPDFStyles.colon}>:</Text>
      <Text style={full ? orgPDFStyles.fullValue : orgPDFStyles.value}>{pdfText(value)}</Text>
    </View>
  );
}

function OrgPDFSection({ title, children, last = false }) {
  return (
    <View style={[orgPDFStyles.section, last ? orgPDFStyles.lastSection : null]}>
      <Text style={orgPDFStyles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function OrgPDFTableCell({ children, width, header = false, center = false }) {
  return (
    <Text
      style={[
        header ? orgPDFStyles.historyHeaderCell : orgPDFStyles.historyCell,
        { width, textAlign: center ? "center" : "left" },
      ]}
    >
      {children}
    </Text>
  );
}

function OrgPengurusPDFTable({ items = [] }) {
  return (
    <OrgPDFSection title="Pengurus Aktif" last>
      {items.length < 1 ? (
        <Text style={orgPDFStyles.muted}>Pengurus aktif belum tersedia.</Text>
      ) : (
        <View style={orgPDFStyles.historyTable}>
          <View style={orgPDFStyles.historyHeaderRow}>
            <OrgPDFTableCell width={orgPengurusColWidth.no} header center>No</OrgPDFTableCell>
            <OrgPDFTableCell width={orgPengurusColWidth.nama} header>Nama</OrgPDFTableCell>
            <OrgPDFTableCell width={orgPengurusColWidth.jabatan} header>Jabatan</OrgPDFTableCell>
            <OrgPDFTableCell width={orgPengurusColWidth.hp} header>HP</OrgPDFTableCell>
          </View>
          {items.map((row, index) => (
            <View key={row?.id_pengurus_organisasi || index} style={orgPDFStyles.historyRow} wrap={false}>
              <OrgPDFTableCell width={orgPengurusColWidth.no} center>{index + 1}</OrgPDFTableCell>
              <OrgPDFTableCell width={orgPengurusColWidth.nama}>{pdfText(formatPengurusName(row))}</OrgPDFTableCell>
              <OrgPDFTableCell width={orgPengurusColWidth.jabatan}>{pdfText(row?.jabatan)}</OrgPDFTableCell>
              <OrgPDFTableCell width={orgPengurusColWidth.hp}>{pdfText(row?.hp)}</OrgPDFTableCell>
            </View>
          ))}
        </View>
      )}
    </OrgPDFSection>
  );
}

function OrganizationDetailPDF({ item }) {
  const logoUrl = item?.logo_data_url || "";
  const isKlub = String(item?.jenis_kode || "").toUpperCase() === "KLUB";
  const pengurusAktif = activePengurusItems(item);

  return (
    <View>
      <Text style={orgPDFStyles.title}>PROFIL ORGANISASI</Text>
      <View style={orgPDFStyles.logoCenterWrap}>
        {logoUrl ? (
          <PdfImage
            src={logoUrl}
            style={orgPDFStyles.logoCentered}
            cache={false}
          />
        ) : (
          <View style={orgPDFStyles.logoCenterPlaceholder}>
            <Text style={orgPDFStyles.muted}>Logo</Text>
          </View>
        )}
      </View>
      <OrgPDFSection title="Data Organisasi">
        <OrgPDFRow label="Nama Organisasi" value={item?.nama} full />
        <OrgPDFRow label="Singkatan" value={item?.nama_singkat} full />
        {isKlub ? <OrgPDFRow label="Induk Organisasi" value={item?.parent_name} full /> : null}
        <OrgPDFRow label="Cabor" value={item?.cabor_name} full />
        <OrgPDFRow label="Alamat" value={item?.alamat} full />
        <OrgPDFRow label="Telepon" value={item?.telepon} full />
        <OrgPDFRow label="Email" value={item?.email} full />
        <OrgPDFRow label="Website" value={item?.website} full />
      </OrgPDFSection>
      <OrgPengurusPDFTable items={pengurusAktif} />
    </View>
  );
}

export default function OrganizationManagementPage({
  title,
  description,
  listUrl,
  jenisKode,
  defaultTingkatKode = "",
  requiredFields = [
    "nama",
    "nama_singkat",
    "alamat",
    "telepon",
    "email",
  ],
  fieldLayout = {},
  showTingkatField = true,
  showParentField = true,
  showCaborField = true,
  caborFieldLabel = "Cabang Olahraga yang Dikelola",
  enableVerification = true,
  listColumnsOverride = null,
  parentFilter = null,
  disableDetail = false,
}) {
  const { user, replaceUser } = useAuth();
  const [showMsg, confirmMsg] = useMsgBox();
  const [caborResp] = useAxios({
    method: "GET",
    url: "/references/cabor",
    auth: true,
  });
  const [jenisResp] = useAxios({
    method: "GET",
    url: "/references/jenis-organisasi",
    auth: true,
  });
  const [tingkatResp] = useAxios({
    method: "GET",
    url: "/references/tingkat-organisasi",
    auth: true,
  });
  const [organisasiResp] = useAxios({
    method: "GET",
    url: "/references/organisasi",
    auth: true,
  });
  const [, , , axiosFetch] = useAxiosFunction();
  const [getDetailPDFViewer, toggleDetailPDFViewer] = usePDFRenderer();
  const [verificationOrg, setVerificationOrg] = useState(null);
  const [listReload, setListReload] = useState(0);
  const [selectedParentID, setSelectedParentID] = useState("");
  const [detailPDFItem, setDetailPDFItem] = useState(null);
  const [detailOrganizationItem, setDetailOrganizationItem] = useState(null);
  const [loadingDetailPDF, setLoadingDetailPDF] = useState(false);

  const cabor = Array.isArray(caborResp?.data) ? caborResp.data : [];
  const jenisOrganisasi = Array.isArray(jenisResp?.data) ? jenisResp.data : [];
  const tingkatOrganisasi = Array.isArray(tingkatResp?.data)
    ? tingkatResp.data
    : [];
  const organisasi = Array.isArray(organisasiResp?.data)
    ? organisasiResp.data
    : [];

  const selectedJenis = useMemo(
    () => findOptionByCode(jenisOrganisasi, jenisKode),
    [jenisOrganisasi, jenisKode],
  );
  const selectedTingkat = useMemo(
    () => findOptionByCode(tingkatOrganisasi, defaultTingkatKode),
    [tingkatOrganisasi, defaultTingkatKode],
  );
  const groupName = String(user?.groupname || "").toUpperCase();
  const isPersonalGroup = groupName === "PERSONAL";
  const isKoniKabkotaPage =
    String(jenisKode || "").toUpperCase() === "KONI" &&
    String(defaultTingkatKode || "").toUpperCase() === "KABUPATEN_KOTA";
  const hideVerificationAction =
    groupName === "ADMIN_KONI_PROVINSI" && isKoniKabkotaPage;
  const showVerificationAction = enableVerification && !hideVerificationAction;
  const canLoginAsKoniKabkota =
    groupName === "ADMIN_KONI_PROVINSI" && isKoniKabkotaPage;
  const allowDetail = !disableDetail && !isPersonalGroup;
  const isKoniKabkotaManagingKlub =
    String(jenisKode || "").toUpperCase() === "KLUB" &&
    groupName === "ADMIN_KONI_KABKOTA";
  const effectiveShowParentField = showParentField || isKoniKabkotaManagingKlub;
  const effectiveShowCaborColumn =
    showCaborField || isKoniKabkotaManagingKlub;
  const canManage = hasPermission(user, "org.manage");
  const canDelete = canManage && !["ADMIN_CABOR", "ADMIN_KLUB"].includes(groupName);
  const statusAktifOptions = [
    { id: "true", nama: "Aktif" },
    { id: "false", nama: "Nonaktif" },
  ];

  const parentOptions = useMemo(() => {
    const items = parentFilter ? organisasi.filter(parentFilter) : organisasi;
    const emptyOption = isKoniKabkotaManagingKlub
      ? []
      : [
          {
            id_organisasi: "",
            __label: "-Tanpa Parent-",
            __value: "",
          },
        ];
    return emptyOption.concat(
      toDropdownOptions(items, "id_organisasi", (item) => {
        if (isKoniKabkotaManagingKlub) {
          return item?.nama || item?.nama_singkat || "";
        }
        return item?.nama || "";
      }),
    );
  }, [isKoniKabkotaManagingKlub, organisasi, parentFilter]);

  const baseCaborOptions = useMemo(
    () =>
      toDropdownOptions(cabor, "id_cabor", (item) => item?.nama || ""),
    [cabor],
  );

  const selectedParentCaborIDs = useMemo(() => {
    if (!isKoniKabkotaManagingKlub || !selectedParentID) return [];
    const parent = parentOptions.find(
      (item) => String(item?.id_organisasi || "") === selectedParentID,
    );
    return normalizeUUIDList(parent?.id_cabor);
  }, [isKoniKabkotaManagingKlub, parentOptions, selectedParentID]);

  const caborOptions = useMemo(() => {
    if (!isKoniKabkotaManagingKlub) return baseCaborOptions;
    if (!selectedParentID) return [];
    const parent = parentOptions.find(
      (item) => String(item?.id_organisasi || "") === selectedParentID,
    );
    const caborNames = splitCSVNames(parent?.cabor_name);
    const optionByID = new Map(
      baseCaborOptions.map((item) => [String(item.__value || item.id_cabor || ""), item]),
    );
    return selectedParentCaborIDs
      .map((id, index) => {
        const existing = optionByID.get(String(id));
        if (existing) return existing;
        const name = caborNames[index] || "Cabor";
        return {
          id_cabor: id,
          nama: name,
          aktif: true,
          __value: id,
          __label: name,
        };
      })
      .filter((item) => item?.id_cabor);
  }, [
    baseCaborOptions,
    isKoniKabkotaManagingKlub,
    parentOptions,
    selectedParentCaborIDs,
    selectedParentID,
  ]);

  const tingkatOptions = useMemo(
    () =>
      toDropdownOptions(
        tingkatOrganisasi,
        "id_tingkat_organisasi",
        (item) => item?.nama || "",
      ),
    [tingkatOrganisasi],
  );

  const coltype = useMemo(() => {
    const requiredFieldSet = new Set(requiredFields);
    const required = (fieldName) =>
      requiredFieldSet.has(fieldName) ? RuleRequired : undefined;
    const colsize = (fieldName, defaultSize) =>
      fieldLayout?.[fieldName]?.colsize || defaultSize;
    const config = {
      id_organisasi: { type: "hidden", name: "id_organisasi" },
      id_jenis_organisasi: {
        type: "hidden",
        name: "id_jenis_organisasi",
        value: selectedJenis?.id_jenis_organisasi
          ? String(selectedJenis.id_jenis_organisasi)
          : "",
      },
      id_tingkat_organisasi: showTingkatField
        ? {
            label: "Tingkat Organisasi",
            type: "dropdown",
            name: "id_tingkat_organisasi",
            colsize: 4,
            validation: RuleRequired,
            options: tingkatOptions,
            valueId: "__value",
            labelId: "__label",
            placeholder: "-Pilih Tingkat-",
          }
        : {
            type: "hidden",
            name: "id_tingkat_organisasi",
            value: selectedTingkat?.id_tingkat_organisasi
              ? String(selectedTingkat.id_tingkat_organisasi)
              : "",
          },
      id_organisasi_induk: {
        ...(effectiveShowParentField
          ? {
              label: isKoniKabkotaManagingKlub
                ? "Induk Organisasi Cabor"
                : "Organisasi Induk",
              type: "dropdown",
              name: "id_organisasi_induk",
              colsize: isKoniKabkotaManagingKlub ? 12 : 6,
              validation: isKoniKabkotaManagingKlub
                ? RuleRequired
                : undefined,
              options: parentOptions,
              valueId: "id_organisasi",
              labelId: "__label",
              placeholder: isKoniKabkotaManagingKlub
                ? "-Pilih Induk Organisasi Cabor-"
                : "-Tanpa Parent-",
            }
          : {
              type: "hidden",
              name: "id_organisasi_induk",
              value: "",
            }),
      },
      id_cabor: {
        label: caborFieldLabel,
        type: "popupchecklist",
        name: "id_cabor",
        colsize: colsize("id_cabor", 12),
        validation: showCaborField ? RuleRequired : undefined,
        options: caborOptions,
        valueId: "id_cabor",
        labelId: "nama",
        buttonLabel: "Pilih Cabor",
        dialogTitle: caborFieldLabel,
        addOnly: true,
        optionsUrl: isKoniKabkotaManagingKlub ? "" : "/references/cabor",
        parentField: isKoniKabkotaManagingKlub ? "id_organisasi_induk" : "",
        parentOptions: isKoniKabkotaManagingKlub ? parentOptions : [],
        parentValueId: "id_organisasi",
        parentChildIdsField: "id_cabor",
        parentChildNamesField: "cabor_name",
        columns: [
          { name: "nama", label: "Cabor", width: 420 },
        ],
      },
      nama: {
        label: "Nama Organisasi",
        type: "text",
        name: "nama",
        colsize: colsize("nama", 8),
        validation: RuleRequired,
        width: 240,
      },
      nama_singkat: {
        label: "Singkatan",
        type: "text",
        name: "nama_singkat",
        colsize: colsize("nama_singkat", 4),
        validation: required("nama_singkat"),
        width: 140,
      },
      alamat: {
        label: "Alamat",
        type: "textarea",
        name: "alamat",
        colsize: colsize("alamat", 12),
        validation: required("alamat"),
        renderFunc: (val) => showEmpty(val),
        width: 240,
      },
      telepon: {
        label: "Telepon",
        type: "text",
        name: "telepon",
        colsize: colsize("telepon", 4),
        validation: required("telepon"),
        width: 140,
      },
      email: {
        label: "Email",
        type: "text",
        name: "email",
        colsize: colsize("email", 4),
        validation: required("email"),
        width: 220,
      },
      website: {
        label: "Web",
        type: "text",
        name: "website",
        colsize: 4,
        align: "center",
        renderFunc: (val) => {
          const url = toExternalUrl(val);
          return url ? (
            <a href={url} target="_blank" rel="noreferrer">
              Lihat
            </a>
          ) : (
            showEmpty(val)
          );
        },
        renderExportFunc: (val) => {
          const url = toExternalUrl(val);
          return url
            ? { __exportType: "link", href: url, label: "Lihat" }
            : "";
        },
        width: 80,
        pdfwidth: 6,
      },
      logo: {
        label: "Logo",
        type: "photodrop",
        name: "logo",
        colsize: colsize("logo", 12),
        url: "/file/upload",
        data: { location: "organisasi-logo", name: "logo" },
        dropname: "logo",
        caption: "Format jpg, jpeg, atau png.",
        previewWidth: 80,
        previewHeight: 80,
        renderFunc: (val) =>
          val ? (
            <img
              alt="Logo organisasi"
              src={toAssetUrl("organisasi-logo", val)}
              style={{
                display: "block",
                width: 36,
                height: 36,
                margin: "0 auto",
                objectFit: "contain",
                border: "1px solid #ddd",
                borderRadius: 4,
                background: "#fff",
              }}
            />
          ) : (
            showEmpty(val)
          ),
        renderExportFunc: (val, colname, item, coltype, exportMode) => {
          const url = toAssetUrl("organisasi-logo", val);
          if (!url) return "";
          if (exportMode === "pdf") {
            return {
              __exportType: "image",
              src: url,
              href: url,
              label: "Lihat",
              width: 28,
              height: 28,
            };
          }
          return { __exportType: "link", href: url, label: "Lihat" };
        },
        width: 80,
        pdfwidth: 6,
        align: "center",
      },
      aktif: {
        label: "Status",
        type: "checkbox",
        name: "aktif",
        colsize: 3,
        renderFunc: (val) => boolText(val),
        width: 100,
        align: "center",
      },
      parent_name: { label: "Induk", renderFunc: (val) => showEmpty(val) },
      cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val) },
      tingkat_name: { label: "Tingkat", renderFunc: (val) => showEmpty(val) },
      verification_status_name: {
        label: "Status Verifikasi",
        renderFunc: (val, col, item) => {
          const code = String(
            item?.verification_status_code || "DRAFT",
          ).toUpperCase();
          const badgeClass =
            code === "DIAJUKAN"
              ? "badge badge-primary"
              : code === "PERLU_PERBAIKAN"
                ? "badge badge-warning"
                : code === "DITOLAK"
                  ? "badge badge-danger"
                  : code === "TERVERIFIKASI" ||
                      code === "TERVERIFIKASI_CABOR" ||
                      code === "TERVERIFIKASI_KONI_DAERAH" ||
                      code === "TERVERIFIKASI_KONI_PROVINSI"
                    ? "badge badge-success"
                    : "badge badge-secondary";
          return <span className={badgeClass}>{showEmpty(val, "Draft")}</span>;
        },
        width: 170,
        align: "center",
      },
      action: {
        label: "Aksi",
        align: "center",
        width: canLoginAsKoniKabkota ? 170 : 150,
      },
    };

    if (!showCaborField) {
      config.id_cabor = {
        type: "hidden",
        name: "id_cabor",
        value: "",
      };
    }

    return config;
  }, [
    caborOptions,
    caborFieldLabel,
    fieldLayout,
    parentOptions,
    requiredFields,
    selectedJenis,
    selectedTingkat,
    canLoginAsKoniKabkota,
    effectiveShowParentField,
    effectiveShowCaborColumn,
    isKoniKabkotaManagingKlub,
    showCaborField,
    showTingkatField,
    tingkatOptions,
  ]);

  const saveHandle = async (form, defaultValue = {}) => {
    const id = form?.id_organisasi || defaultValue?.id_organisasi || "";
    const method = id ? "PUT" : "POST";
    const url = id ? `/organisasi/${id}` : "/organisasi";

    return axiosFetch({
      method,
      url,
      auth: true,
      data: {
        id_organisasi_induk: form?.id_organisasi_induk || "",
        id_cabor: showCaborField ? normalizeUUIDList(form?.id_cabor) : [],
        id_jenis_organisasi: Number(
          selectedJenis?.id_jenis_organisasi || form?.id_jenis_organisasi || 0,
        ),
        id_tingkat_organisasi: Number(
          showTingkatField
            ? form?.id_tingkat_organisasi || 0
            : selectedTingkat?.id_tingkat_organisasi || 0,
        ),
        nama: form?.nama || "",
        nama_singkat: form?.nama_singkat || "",
        alamat: form?.alamat || "",
        telepon: form?.telepon || "",
        email: form?.email || "",
        website: form?.website || "",
        logo: form?.logo || "",
        aktif: id ? undefined : true,
      },
    });
  };

  const onOrganizationFormChange = (setValue, form) => {
    if (!isKoniKabkotaManagingKlub) return;

    const nextParentID = String(form?.id_organisasi_induk || "").trim();
    if (nextParentID !== selectedParentID) {
      setSelectedParentID(nextParentID);
    }

    const parent = parentOptions.find(
      (item) => String(item?.id_organisasi || "") === nextParentID,
    );
    const allowedIDs = normalizeUUIDList(parent?.id_cabor);
    const selectedIDs = normalizeUUIDList(form?.id_cabor);

    if (selectedIDs.length === 0) return;
    const filteredIDs = selectedIDs.filter((id) => allowedIDs.includes(id));
    if (filteredIDs.length !== selectedIDs.length) {
      setValue("id_cabor", filteredIDs);
    }
  };

  const addColumns = [
    "id_jenis_organisasi",
    "id_tingkat_organisasi",
    "id_organisasi_induk",
    "id_cabor",
    "nama",
    "nama_singkat",
    "alamat",
    "telepon",
    "email",
    "website",
    "logo",
  ].filter((name) => {
    if (!showCaborField && name === "id_cabor") return false;
    if (!effectiveShowParentField && name === "id_organisasi_induk") return false;
    return true;
  });

  const defaultListColumns = [
    "no",
    "logo",
    "nama_singkat",
    "nama",
    "cabor_name",
    "alamat",
    "telepon",
    "aktif",
    "action",
  ];

  const exportFullColumns = [
    "no",
    "logo",
    "nama_singkat",
    "nama",
    "cabor_name",
    "alamat",
    "telepon",
    "email",
    "website",
    "aktif",
  ];

  const listColumns = (
    Array.isArray(listColumnsOverride) && listColumnsOverride.length > 0
      ? listColumnsOverride
      : defaultListColumns
  ).filter((name) => {
    if (!allowDetail && name === "action") return false;
    if (!effectiveShowCaborColumn && name === "cabor_name") return false;
    if (
      !effectiveShowParentField &&
      name === "parent_name" &&
      !(Array.isArray(listColumnsOverride) && listColumnsOverride.includes("parent_name"))
    ) {
      return false;
    }
    if (!showTingkatField && name === "tingkat_name") return false;
    if (!enableVerification && name === "verification_status_name") {
      return false;
    }
    return true;
  });

  const openVerificationDialog = (item) => {
    setVerificationOrg({
      id: item?.id_organisasi || "",
      name: item?.nama || "",
    });
  };

  const loginAsKoniKabkota = async (item) => {
    const organizationName = item?.nama || item?.nama_singkat || "KONI Kab/Kota ini";
    const confirmed = await confirmMsg(
      "Login sebagai Admin KONI Kab/Kota",
      `Anda akan masuk dan bertindak sebagai Admin ${organizationName}. Lanjutkan?`,
    );
    if (!confirmed) return;

    const result = await axiosFetch({
      method: "POST",
      url: "/auth/login-as/koni-kabkota",
      auth: true,
      data: {
        id_organisasi: item?.id_organisasi || "",
      },
    });
    if (!result?.data?.success || !result?.data?.token) {
      await showMsg("Login sebagai gagal", result?.message || "Akun Admin KONI Kab/Kota belum dapat digunakan.");
      return;
    }

    await replaceUser(result.data);
    window.location.href = "/";
  };

  const openDetailPDF = async (idDetail) => {
    const currentID = String(detailOrganizationItem?.id_organisasi || "");
    let detailItem = currentID === String(idDetail || "") ? detailOrganizationItem : null;
    setLoadingDetailPDF(true);
    try {
      if (!detailItem) {
        const result = await axiosFetch({
          method: "GET",
          url: `/organisasi/${idDetail}`,
          auth: true,
        });
        detailItem = result?.data || null;
      }
      if (!detailItem) return;

      const logoDataURL = await loadOrganizationLogoDataURL(detailItem?.logo, user?.token);
      setDetailPDFItem({
        ...detailItem,
        logo_data_url: logoDataURL,
      });
      toggleDetailPDFViewer();
    } finally {
      setLoadingDetailPDF(false);
    }
  };

  const listFilter = [
    { dtype: "q", name: "q", colsize: isKoniKabkotaManagingKlub ? 3 : 4 },
    ...(isKoniKabkotaManagingKlub
      ? [
          {
            dtype: "dropdown",
            name: "id_cabor",
            label: "Cabor",
            colsize: 3,
            options: caborOptions,
            valueId: "__value",
            labelId: "__label",
            placeholder: "-Semua Cabor-",
          },
        ]
      : []),
    {
      dtype: "dropdown",
      name: "aktif",
      label: "Status",
      colsize: isKoniKabkotaManagingKlub ? 3 : 3,
      options: statusAktifOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Semua Status-",
    },
  ];

  return (
    <>
      <DataList
        title={title}
        coltype={coltype}
        pdfconfig={{ orientation: "landscape" }}
        dlist={{
          url: listUrl,
          columns: listColumns,
          filter: listFilter,
          sort_by: [
            "nama",
            "nama_singkat",
            "cabor_name",
            "parent_name",
            "tingkat_name",
          ],
          render_action_extension:
            showVerificationAction || canLoginAsKoniKabkota
              ? (val, col, item) => (
                <>
                  {showVerificationAction ? (
                    <>
                      <a
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          openVerificationDialog(item);
                        }}
                        className="btn btn-info btn-xs"
                        title="Verifikasi"
                      >
                        <i className="fa fa-check-circle" />
                      </a>
                      &nbsp;
                    </>
                  ) : null}
                  {canLoginAsKoniKabkota ? (
                    <>
                      <a
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          loginAsKoniKabkota(item);
                        }}
                        className="btn btn-warning btn-xs"
                        title="Login sebagai Admin KONI Kab/Kota"
                      >
                        <i className="fa fa-sign-in-alt" />
                      </a>
                      &nbsp;
                    </>
                  ) : null}
                </>
              )
              : null,
        }}
        reload={listReload}
        addconfig={
          canManage
            ? {
                columns: addColumns,
                title: `Tambah ${title}`,
                label_tool: `Tambah ${title}`,
                defaultValue: {},
                saveHandle,
                onChangeForm: onOrganizationFormChange,
              }
            : null
        }
        editconfig={
          canManage
            ? {
                columns: ["id_organisasi"].concat(addColumns),
                title: `Ubah ${title}`,
                url: "/organisasi",
                urlitem: "/organisasi",
                cellid: "id_organisasi",
                saveHandle,
                onChangeForm: onOrganizationFormChange,
                normalizeItem: (item) => ({
                  ...item,
                  id_jenis_organisasi: item?.id_jenis_organisasi
                    ? String(item.id_jenis_organisasi)
                    : String(selectedJenis?.id_jenis_organisasi || ""),
                  id_tingkat_organisasi: item?.id_tingkat_organisasi
                    ? String(item.id_tingkat_organisasi)
                    : String(selectedTingkat?.id_tingkat_organisasi || ""),
                  id_organisasi_induk: item?.id_organisasi_induk
                    ? String(item.id_organisasi_induk)
                    : "",
                  id_cabor: normalizeUUIDList(item?.id_cabor),
                }),
              }
            : null
        }
        detailconfig={
          allowDetail
            ? {
                url: "/organisasi",
                cellid: "id_organisasi",
                title: "Detail Organisasi",
                size: "xl",
                tooltip: "Detail",
                icon: "fa-search",
                renderContent: (item) => (
                  <OrganizationDetail
                    item={item}
                    onLoaded={setDetailOrganizationItem}
                  />
                ),
                footerContent: ({ idDetail }) => (
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={loadingDetailPDF}
                    onClick={() => openDetailPDF(idDetail)}
                  >
                    <i className="fa fa-file-pdf mr-1" />
                    {loadingDetailPDF ? "Memuat PDF..." : "PDF"}
                  </button>
                ),
              }
            : null
        }
        pdflist={{ title, columns: listColumns.filter((name) => name !== "action") }}
        xlslist={{ title, columns: exportFullColumns.filter((name) => {
          if (!effectiveShowCaborColumn && name === "cabor_name") return false;
          return true;
        }) }}
        delconfig={
          canDelete
            ? {
                url: "/organisasi",
                cellid: "id_organisasi",
                cellname: "nama",
              }
            : null
        }
        activateconfig={
          canManage
            ? {
                url: "/organisasi",
                cellid: "id_organisasi",
                cellname: "nama",
                cellactivate: "aktif",
                labelactivate: "Aktifkan",
                labelinactivate: "Non-aktifkan",
                cfmactivate: "mengaktifkan",
                cfminactivate: "menonaktifkan",
                infoactivate: "diaktifkan",
                infoinactivate: "dinonaktifkan",
              }
            : null
        }
      />
      {enableVerification ? (
        <OrganizationVerificationDialog
          show={Boolean(verificationOrg?.id)}
          organizationId={verificationOrg?.id || ""}
          organizationName={verificationOrg?.name || ""}
          canManage={canManage}
          closeHandle={() => setVerificationOrg(null)}
          onSubmitted={() => setListReload((value) => value + 1)}
        />
      ) : null}
      {detailPDFItem
        ? getDetailPDFViewer({
            title: `Profil Organisasi - ${detailPDFItem?.nama || ""}`,
            content: <OrganizationDetailPDF item={detailPDFItem} />,
            viewerKey: `org-detail-${detailPDFItem?.id_organisasi || ""}-${Date.now()}`,
            orientation: "portrait",
          })
        : null}
    </>
  );
}
